import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const quillModules = {
  toolbar: [
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = ['size', 'bold', 'italic', 'underline', 'color', 'background', 'list', 'bullet', 'align', 'link', 'image', 'video', 'header'];

const WEIGHT_OPTIONS = [
  { value: 100, label: 'Thin' },
  { value: 200, label: 'Extra-Light' },
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semi-Bold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'Extra-Bold' },
  { value: 900, label: 'Black' }
];

const AlignSelector = ({ name, value, onChange }) => (
  <div className="form-group">
    <label>Position dans le header</label>
    <div className="form-row" style={{gap: '15px'}}>
      <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}>
        <input type="radio" name={name} value="left" checked={value === 'left'} onChange={onChange} />
        Gauche
      </label>
      <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}>
        <input type="radio" name={name} value="center" checked={value === 'center'} onChange={onChange} />
        Centre
      </label>
      <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}>
        <input type="radio" name={name} value="right" checked={value === 'right'} onChange={onChange} />
        Droite
      </label>
    </div>
  </div>
);

const TextFieldControls = ({ label, namePrefix, form, onChange, onNumberChange, weightOptions }) => (
  <div className="text-field-block">
    <div className="form-group">
      <label>{label}</label>
      {namePrefix === 'message' ? (
        <textarea
          name={namePrefix}
          value={form[namePrefix] || ''}
          onChange={onChange}
          placeholder={`Texte ${label.toLowerCase()}...`}
          rows="3"
        />
      ) : (
        <input
          type="text"
          name={namePrefix}
          value={form[namePrefix] || ''}
          onChange={onChange}
          placeholder={`Texte ${label.toLowerCase()}...`}
        />
      )}
    </div>
    <div className="text-style-row">
      <div className="style-control">
        <label>Taille : {form[`${namePrefix}_size`]}px</label>
        <input
          type="range"
          min="20"
          max="400"
          step="4"
          value={form[`${namePrefix}_size`]}
          onChange={(e) => onNumberChange(`${namePrefix}_size`, e.target.value)}
        />
      </div>
      <div className="style-control">
        <label>Graisse</label>
        <select
          value={form[`${namePrefix}_weight`]}
          onChange={(e) => onNumberChange(`${namePrefix}_weight`, e.target.value)}
        >
          {(weightOptions || []).map(w => (
            <option key={w.value} value={w.value}>{w.value} - {w.label}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

function DisplayPageModal({ page, screens, groups, onSave, onVideoUpload, onLogoUpload, onHeaderLogoUpload, onClose }) {
  const [dailyEvents, setDailyEvents] = useState([]);
  const [uploadingEvents, setUploadingEvents] = useState(false);

  const [htmlModes, setHtmlModes] = useState({});
  const [form, setForm] = useState({
    name: '',
    screen_id: '',
    group_id: '',
    assign_type: 'screen',
    video_type: 'url',
    video_url: '',
    overlay_opacity: 0.35,
    weather_enabled: true,
    weather_lat: 47.37,
    weather_lon: 8.54,
    weather_city: 'Zurich',
    weather_align: 'right',
    clock_enabled: true,
    clock_align: 'center',
    header_logo_type: 'none',
    header_logo_url: '',
    header_logo_size: 120,
    header_logo_align: 'left',
    header_text: '',
    header_text_size: 60,
    header_text_weight: 300,
    logo_type: 'none',
    logo_url: '',
    logo_size: 200,
    title: '',
    title_size: 192,
    title_weight: 700,
    subtitle: '',
    subtitle_size: 120,
    subtitle_weight: 400,
    message: '',
    message_size: 88,
    message_weight: 300,
    linkedin_enabled: false,
    linkedin_show_logo: true,
    linkedin_posts: [],
    linkedin_url: '',
    wifi_enabled: false,
    wifi_ssid: '',
    wifi_password: '',
    wifi_encryption: 'WPA',
    font_family: 'Segoe UI',
    display_margin: 80,
    daily_events_enabled: false,
    is_active: true
  });

  const [videoFile, setVideoFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [headerLogoFile, setHeaderLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);


  useEffect(() => {
    if (page) {
      setForm({
        name: page.name || '',
        screen_id: page.screen_id || '',
        group_id: page.group_id || '',
        assign_type: page.group_id ? 'group' : 'screen',
        video_type: page.video_type || 'url',
        video_url: page.video_url || '',
        overlay_opacity: page.overlay_opacity ?? 0.35,
        weather_enabled: page.weather_enabled ?? true,
        weather_lat: page.weather_lat || 47.37,
        weather_lon: page.weather_lon || 8.54,
        weather_city: page.weather_city || 'Zurich',
        weather_align: page.weather_align || 'right',
        clock_enabled: page.clock_enabled ?? true,
        clock_align: page.clock_align || 'center',
        header_logo_type: page.header_logo_type || 'none',
        header_logo_url: page.header_logo_url || '',
        header_logo_size: page.header_logo_size || 120,
        header_logo_align: page.header_logo_align || 'left',
        header_text: page.header_text || '',
        header_text_size: page.header_text_size || 60,
        header_text_weight: page.header_text_weight || 300,
        logo_type: page.logo_type || 'none',
        logo_url: page.logo_url || '',
        logo_size: page.logo_size || 200,
        title: page.title || '',
        title_size: page.title_size || 192,
        title_weight: page.title_weight || 700,
        subtitle: page.subtitle || '',
        subtitle_size: page.subtitle_size || 120,
        subtitle_weight: page.subtitle_weight || 400,
        message: page.message || '',
        message_size: page.message_size || 88,
        message_weight: page.message_weight || 300,
        linkedin_enabled: page.linkedin_enabled ?? false,
        linkedin_show_logo: page.linkedin_show_logo ?? true,
        linkedin_posts: page.linkedin_posts || [],
        linkedin_url: page.linkedin_url || '',
        wifi_enabled: page.wifi_enabled ?? false,
        wifi_ssid: page.wifi_ssid || '',
        wifi_password: page.wifi_password || '',
        wifi_encryption: page.wifi_encryption || 'WPA',
        font_family: page.font_family || 'Segoe UI',
        display_margin: page.display_margin ?? 80,
        daily_events_enabled: page.daily_events_enabled ?? false,
        is_active: page.is_active ?? true
      });
    }
  }, [page]);

  // Load daily events when editing
  useEffect(() => {
    if (page && page.id) {
      api.get('/display/' + page.id + '/daily-events')
        .then(r => setDailyEvents(r.data))
        .catch(() => {});
    }
  }, [page]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingEvents(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/display/' + page.id + '/daily-events/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDailyEvents([]);
      const evts = await api.get('/display/' + page.id + '/daily-events');
      setDailyEvents(evts.data);
      alert(r.data.count + ' evenements importes');
    } catch (err) {
      alert('Erreur import: ' + (err.response?.data?.error || err.message));
    }
    setUploadingEvents(false);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      name: form.name,
      screen_id: form.assign_type === 'screen' ? (form.screen_id || null) : null,
      group_id: form.assign_type === 'group' ? (form.group_id || null) : null,
      video_type: form.video_type,
      video_url: form.video_type === 'url' ? form.video_url : null,
      overlay_opacity: parseFloat(form.overlay_opacity),
      weather_enabled: form.weather_enabled,
      weather_lat: parseFloat(form.weather_lat),
      weather_lon: parseFloat(form.weather_lon),
      weather_city: form.weather_city,
      weather_align: form.weather_align,
      clock_enabled: form.clock_enabled,
      clock_align: form.clock_align,
      header_logo_type: form.header_logo_type,
      header_logo_url: form.header_logo_type === 'url' ? form.header_logo_url : null,
      header_logo_size: parseInt(form.header_logo_size),
      header_logo_align: form.header_logo_align,
      header_text: form.header_text,
      header_text_size: parseInt(form.header_text_size),
      header_text_weight: parseInt(form.header_text_weight),
      logo_type: form.logo_type,
      logo_url: form.logo_type === 'url' ? form.logo_url : null,
      logo_size: parseInt(form.logo_size),
      title: form.title,
      title_size: parseInt(form.title_size),
      title_weight: parseInt(form.title_weight),
      subtitle: form.subtitle,
      subtitle_size: parseInt(form.subtitle_size),
      subtitle_weight: parseInt(form.subtitle_weight),
      message: form.message,
      message_size: parseInt(form.message_size),
      message_weight: parseInt(form.message_weight),
      linkedin_enabled: form.linkedin_enabled,
      linkedin_show_logo: form.linkedin_show_logo,
      linkedin_posts: form.linkedin_posts,
      linkedin_url: form.linkedin_url,
      wifi_enabled: form.wifi_enabled,
      wifi_ssid: form.wifi_ssid,
      wifi_password: form.wifi_password,
      wifi_encryption: form.wifi_encryption,
      font_family: form.font_family,
      display_margin: parseInt(form.display_margin),
      daily_events_enabled: form.daily_events_enabled,
      is_active: form.is_active
    };

    await onSave(data);

    if (videoFile && page?.id) {
      await onVideoUpload(page.id, videoFile);
    }
    if (logoFile && page?.id) {
      await onLogoUpload(page.id, logoFile);
    }
    if (headerLogoFile && page?.id) {
      await onHeaderLogoUpload(page.id, headerLogoFile);
    }

    setSaving(false);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setForm(prev => ({ ...prev, video_type: 'upload' }));
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setForm(prev => ({ ...prev, logo_type: 'upload' }));
    }
  };

  const handleHeaderLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderLogoFile(file);
      setForm(prev => ({ ...prev, header_logo_type: 'upload' }));
    }
  };

  const weightOptions = [
    { value: 100, label: 'Thin' },
    { value: 200, label: 'Extra Light' },
    { value: 300, label: 'Light' },
    { value: 400, label: 'Regular' },
    { value: 500, label: 'Medium' },
    { value: 600, label: 'Semi Bold' },
    { value: 700, label: 'Bold' },
    { value: 800, label: 'Extra Bold' },
    { value: 900, label: 'Black' }
  ];



  const cities = [
    { name: 'Zurich', lat: 47.37, lon: 8.54 },
    { name: 'Geneve', lat: 46.20, lon: 6.14 },
    { name: 'Berne', lat: 46.95, lon: 7.45 },
    { name: 'Lausanne', lat: 46.52, lon: 6.63 },
    { name: 'Bale', lat: 47.56, lon: 7.59 },
    { name: 'Lucerne', lat: 47.05, lon: 8.31 },
    { name: 'Lugano', lat: 46.00, lon: 8.95 },
    { name: 'Sion', lat: 46.23, lon: 7.36 },
    { name: 'Fribourg', lat: 46.81, lon: 7.15 },
    { name: 'Neuchatel', lat: 46.99, lon: 6.93 }
  ];

  const handleCityChange = (e) => {
    const cityName = e.target.value;
    const city = cities.find(c => c.name === cityName);
    if (city) {
      setForm(prev => ({
        ...prev,
        weather_city: city.name,
        weather_lat: city.lat,
        weather_lon: city.lon
      }));
    } else {
      setForm(prev => ({ ...prev, weather_city: cityName }));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{page ? 'Modifier la page' : 'Nouvelle page d\'affichage'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* === NOM === */}
            <div className="form-section">
              <h3>General</h3>
              <div className="form-group">
                <label>Nom de la page</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Ex: Accueil Salle A"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                    {' '}Page active
                  </label>
                </div>
              </div>
            </div>

            {/* === ASSIGNATION === */}
            <div className="form-section">
              <h3>Assignation</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input type="radio" name="assign_type" value="screen" checked={form.assign_type === 'screen'} onChange={handleChange} />
                    {' '}Ecran specifique
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input type="radio" name="assign_type" value="group" checked={form.assign_type === 'group'} onChange={handleChange} />
                    {' '}Groupe
                  </label>
                </div>
              </div>
              {form.assign_type === 'screen' ? (
                <div className="form-group">
                  <label>Ecran</label>
                  <select name="screen_id" value={form.screen_id} onChange={handleChange}>
                    <option value="">-- Selectionner un ecran --</option>
                    {screens.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.pi_id})</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Groupe</label>
                  <select name="group_id" value={form.group_id} onChange={handleChange}>
                    <option value="">-- Selectionner un groupe --</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* === VIDEO === */}
            <div className="form-section">
              <h3>Video de fond</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input type="radio" name="video_type" value="url" checked={form.video_type === 'url'} onChange={handleChange} />
                    {' '}URL externe
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input type="radio" name="video_type" value="upload" checked={form.video_type === 'upload'} onChange={handleChange} />
                    {' '}Upload fichier
                  </label>
                </div>
              </div>
              {form.video_type === 'url' ? (
                <div className="form-group">
                  <label>URL de la video</label>
                  <input type="url" name="video_url" value={form.video_url} onChange={handleChange} placeholder="https://example.com/video.mp4" />
                </div>
              ) : (
                <div className="form-group">
                  <label>Fichier video (mp4, webm, ogg)</label>
                  <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={handleVideoFileChange} />
                  {page?.video_filename && <small>Video actuelle : {page.video_filename}</small>}
                  {!page && <small style={{color: '#999'}}>Sauvegardez d'abord, puis uploadez via modifier.</small>}
                </div>
              )}
              <div className="form-group">
                <label>Opacite overlay : {Math.round(form.overlay_opacity * 100)}%</label>
                <input type="range" name="overlay_opacity" min="0" max="1" step="0.05" value={form.overlay_opacity} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Marge ecran : {form.display_margin}px</label>
                <input type="range" name="display_margin" min="0" max="200" step="5" value={form.display_margin} onChange={handleChange} />
              </div>

              <div className="form-group">
                <label>
                  <input type="checkbox" name="daily_events_enabled" checked={form.daily_events_enabled} onChange={handleChange} />
                  {' '}Evenements du jour (remplace le contenu central)
                </label>
              </div>

              {form.daily_events_enabled && (
                <div className="form-group">
                  <label>Importer un fichier Excel (.xlsx)</label>
                  <p style={{fontSize: '12px', opacity: 0.7, margin: '4px 0 8px'}}>
                    Format: Date | Titre | Description (1 ligne par jour)
                  </p>
                  <input type="file" accept=".xlsx,.xls" onChange={handleUploadExcel} disabled={uploadingEvents || !page?.id} />
                  {!page?.id && <p style={{fontSize: '12px', color: '#e57373'}}>Sauvegardez d'abord la page avant d'importer</p>}
                  {dailyEvents.length > 0 && (
                    <p style={{fontSize: '12px', marginTop: 8, color: '#7d96a1'}}>
                      {dailyEvents.length} evenements charges (du {dailyEvents[0]?.event_date} au {dailyEvents[dailyEvents.length-1]?.event_date})
                    </p>
                  )}
                </div>
              )}

              <div className="form-group" style={{display: 'none'}}>
              </div>
            </div>

            {/* === METEO === */}
            <div className="form-section">
              <h3>Meteo</h3>
              <div className="form-group">
                <label>
                  <input type="checkbox" name="weather_enabled" checked={form.weather_enabled} onChange={handleChange} />
                  {' '}Afficher la meteo (avec icone conditions)
                </label>
              </div>
              {form.weather_enabled && (
                <>
                  <div className="form-group">
                    <label>Ville</label>
                    <select value={form.weather_city} onChange={handleCityChange}>
                      {cities.map(c => (<option key={c.name} value={c.name}>{c.name}</option>))}
                      <option value="custom">Personnalise...</option>
                    </select>
                  </div>
                  {!cities.find(c => c.name === form.weather_city) && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Ville (nom)</label>
                        <input type="text" name="weather_city" value={form.weather_city} onChange={handleChange} />
                      </div>
                      <div className="form-group">
                        <label>Latitude</label>
                        <input type="number" name="weather_lat" value={form.weather_lat} onChange={handleChange} step="0.01" />
                      </div>
                      <div className="form-group">
                        <label>Longitude</label>
                        <input type="number" name="weather_lon" value={form.weather_lon} onChange={handleChange} step="0.01" />
                      </div>
                    </div>
                  )}
                  <AlignSelector name="weather_align" value={form.weather_align} onChange={handleChange} />
                </>
              )}
            </div>

            {/* === HORLOGE === */}
            <div className="form-section">
              <h3>Horloge</h3>
              <div className="form-group">
                <label>
                  <input type="checkbox" name="clock_enabled" checked={form.clock_enabled} onChange={handleChange} />
                  {' '}Afficher l'horloge
                </label>
              </div>
              {form.clock_enabled && (
                <AlignSelector name="clock_align" value={form.clock_align} onChange={handleChange} />
              )}
            </div>

            {/* === CONTENU === */}
            <div className="form-section">
              <h3>Contenu</h3>

              {/* HEADER LOGO (Top Bar) */}
              <div className="text-field-block">
                <div className="form-group">
                  <label><strong>Logo en-tete</strong> (barre superieure, entre horloge et meteo)</label>
                  <div className="form-row" style={{marginBottom: '8px'}}>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="header_logo_type" value="none" checked={form.header_logo_type === 'none'} onChange={handleChange} />
                        {' '}Aucun
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="header_logo_type" value="url" checked={form.header_logo_type === 'url'} onChange={handleChange} />
                        {' '}URL
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="header_logo_type" value="upload" checked={form.header_logo_type === 'upload'} onChange={handleChange} />
                        {' '}Upload
                      </label>
                    </div>
                  </div>
                  {form.header_logo_type === 'url' && (
                    <input type="url" name="header_logo_url" value={form.header_logo_url} onChange={handleChange} placeholder="https://example.com/header-logo.png" />
                  )}
                  {form.header_logo_type === 'upload' && (
                    <>
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp" onChange={handleHeaderLogoFileChange} />
                      {page?.header_logo_filename && <small>Logo actuel : {page.header_logo_filename}</small>}
                      {!page && <small style={{color: '#999'}}>Sauvegardez d'abord, puis uploadez via modifier.</small>}
                    </>
                  )}
                </div>
                {form.header_logo_type !== 'none' && (
                  <div className="text-style-row">
                    <div className="style-control">
                      <label>Taille logo en-tete : {form.header_logo_size}px</label>
                      <input
                        type="range"
                        min="30"
                        max="300"
                        step="10"
                        value={form.header_logo_size}
                        onChange={(e) => handleNumberChange('header_logo_size', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {form.header_logo_type === 'url' && form.header_logo_url && (
                  <div style={{marginTop: '10px', textAlign: 'center', background: '#16162a', padding: '15px', borderRadius: '8px'}}>
                    <img src={form.header_logo_url} alt="Preview" style={{maxHeight: '60px', maxWidth: '100%', objectFit: 'contain'}} />
                  </div>
                )}
              </div>

              {/* HEADER TEXT */}
              <TextFieldControls label="Texte en-tete (au-dessus du logo)" namePrefix="header_text" form={form} onChange={handleChange} onNumberChange={handleNumberChange} weightOptions={WEIGHT_OPTIONS} />

              {/* LOGO */}
              <div className="text-field-block">
                <div className="form-group">
                  <label><strong>Logo central</strong></label>
                  <div className="form-row" style={{marginBottom: '8px'}}>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="logo_type" value="none" checked={form.logo_type === 'none'} onChange={handleChange} />
                        {' '}Aucun
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="logo_type" value="url" checked={form.logo_type === 'url'} onChange={handleChange} />
                        {' '}URL
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input type="radio" name="logo_type" value="upload" checked={form.logo_type === 'upload'} onChange={handleChange} />
                        {' '}Upload
                      </label>
                    </div>
                  </div>
                  {form.logo_type === 'url' && (
                    <input type="url" name="logo_url" value={form.logo_url} onChange={handleChange} placeholder="https://example.com/logo.png" />
                  )}
                  {form.logo_type === 'upload' && (
                    <>
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp" onChange={handleLogoFileChange} />
                      {page?.logo_filename && <small>Logo actuel : {page.logo_filename}</small>}
                      {!page && <small style={{color: '#999'}}>Sauvegardez d'abord, puis uploadez via modifier.</small>}
                    </>
                  )}
                </div>
                {form.logo_type !== 'none' && (
                  <div className="text-style-row">
                    <div className="style-control">
                      <label>Taille logo : {form.logo_size}px</label>
                      <input
                        type="range"
                        min="40"
                        max="2000"
                        step="10"
                        value={form.logo_size}
                        onChange={(e) => handleNumberChange('logo_size', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {form.logo_type === 'url' && form.logo_url && (
                  <div style={{marginTop: '10px', textAlign: 'center', background: '#16162a', padding: '15px', borderRadius: '8px'}}>
                    <img src={form.logo_url} alt="Preview" style={{maxHeight: '80px', maxWidth: '100%', objectFit: 'contain'}} />
                  </div>
                )}
              </div>

              <TextFieldControls label="Titre principal" namePrefix="title" form={form} onChange={handleChange} onNumberChange={handleNumberChange} weightOptions={WEIGHT_OPTIONS} />
              <TextFieldControls label="Sous-titre" namePrefix="subtitle" form={form} onChange={handleChange} onNumberChange={handleNumberChange} weightOptions={WEIGHT_OPTIONS} />
              <TextFieldControls label="Message" namePrefix="message" form={form} onChange={handleChange} onNumberChange={handleNumberChange} weightOptions={WEIGHT_OPTIONS} />
            </div>

            {/* === LINKEDIN === */}
            <div className="form-section">
              <h3>LinkedIn</h3>
              <div className="form-group">
                <label>
                  <input type="checkbox" name="linkedin_enabled" checked={form.linkedin_enabled} onChange={handleChange} />
                  {' '}Afficher les posts LinkedIn
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input type="checkbox" name="linkedin_show_logo" checked={form.linkedin_show_logo} onChange={handleChange} />
                  {' '}Afficher le logo LinkedIn
                </label>
              </div>

              <div className="form-group" style={{marginTop: '12px'}}>
                <label>URL page LinkedIn (QR code "Suivez-nous")</label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={form.linkedin_url}
                  onChange={handleChange}
                  placeholder="https://www.linkedin.com/company/votre-entreprise"
                />
                {form.linkedin_url && (
                  <div style={{marginTop: '8px', padding: '12px', background: '#16162a', borderRadius: '8px', fontSize: '13px', color: '#888'}}>
                    QR code "Suivez-nous" genere pour : <strong style={{color: '#0A66C2'}}>{form.linkedin_url}</strong>
                  </div>
                )}
              </div>

              {form.linkedin_enabled && (
                <>
                  {(form.linkedin_posts || []).map((post, idx) => (
                    <div key={idx} className="linkedin-post-block" style={{marginTop: idx > 0 ? '16px' : '0', position: 'relative'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                        <h4 style={{color: '#0A66C2', margin: 0}}>Post {idx + 1}</h4>
                        <button type="button" onClick={() => {
                          const posts = [...form.linkedin_posts];
                          posts.splice(idx, 1);
                          setForm(prev => ({...prev, linkedin_posts: posts}));
                        }} style={{background: '#cc3333', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px'}}>
                          Supprimer
                        </button>
                      </div>
                      <div className="form-group">
                        <label>Date</label>
                        <input type="text" value={post.date || ''} onChange={(e) => {
                          const posts = [...form.linkedin_posts];
                          posts[idx] = {...posts[idx], date: e.target.value};
                          setForm(prev => ({...prev, linkedin_posts: posts}));
                        }} placeholder="Ex: 28 mars 2026" />
                      </div>
                      <div className="form-group">
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <label>Texte du post</label>
                          <button type="button" onClick={() => setHtmlModes(prev => ({...prev, [idx]: !prev[idx]}))} style={{background: htmlModes[idx] ? '#0A66C2' : '#333', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px'}}>
                            {htmlModes[idx] ? 'Visuel' : 'HTML'}
                          </button>
                        </div>
                        {htmlModes[idx] ? (
                          <textarea
                            value={post.text || ''}
                            onChange={(e) => {
                              const posts = [...form.linkedin_posts];
                              posts[idx] = {...posts[idx], text: e.target.value};
                              setForm(prev => ({...prev, linkedin_posts: posts}));
                            }}
                            placeholder="<h2>Titre</h2><p>Contenu...</p><img src=&quot;url&quot;/>"
                            style={{width: '100%', minHeight: '150px', background: '#1a1a2e', color: '#e0e0e0', border: '1px solid #333', borderRadius: '6px', padding: '10px', fontFamily: 'monospace', fontSize: '13px', resize: 'vertical'}}
                          />
                        ) : (
                          <div className="quill-dark-wrapper">
                            <ReactQuill
                              theme="snow"
                              value={post.text || ''}
                              onChange={(value) => {
                                const posts = [...form.linkedin_posts];
                                posts[idx] = {...posts[idx], text: value};
                                setForm(prev => ({...prev, linkedin_posts: posts}));
                              }}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="Contenu du post LinkedIn..."
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Image (URL)</label>
                        <input type="url" value={post.image || ''} onChange={(e) => {
                          const posts = [...form.linkedin_posts];
                          posts[idx] = {...posts[idx], image: e.target.value};
                          setForm(prev => ({...prev, linkedin_posts: posts}));
                        }} placeholder="https://example.com/image.jpg" />
                      </div>
                      {post.image && (
                        <div style={{marginTop: '6px', background: '#16162a', padding: '10px', borderRadius: '8px', textAlign: 'center'}}>
                          <img src={post.image} alt="Preview" style={{maxHeight: '60px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px'}} />
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    setForm(prev => ({...prev, linkedin_posts: [...(prev.linkedin_posts || []), {text: '', image: '', date: ''}]}));
                  }} style={{marginTop: '12px', background: '#0A66C2', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', width: '100%'}}>
                    + Ajouter un post
                  </button>
                </>
              )}
            </div>

            {/* === WIFI GUEST === */}
            <div className="form-section">
              <h3>WiFi Guest</h3>
              <div className="form-group">
                <label>
                  <input type="checkbox" name="wifi_enabled" checked={form.wifi_enabled} onChange={handleChange} />
                  {' '}Afficher le QR code WiFi
                </label>
              </div>

              {form.wifi_enabled && (
                <>
                  <div className="form-group">
                    <label>SSID (nom du reseau)</label>
                    <input
                      type="text"
                      name="wifi_ssid"
                      value={form.wifi_ssid}
                      onChange={handleChange}
                      placeholder="Ex: MonReseau-Guest"
                    />
                  </div>
                  <div className="form-group">
                    <label>Mot de passe</label>
                    <input
                      type="text"
                      name="wifi_password"
                      value={form.wifi_password}
                      onChange={handleChange}
                      placeholder="Mot de passe WiFi"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type de securite</label>
                    <select name="wifi_encryption" value={form.wifi_encryption} onChange={handleChange}>
                      <option value="WPA">WPA/WPA2</option>
                      <option value="WEP">WEP</option>
                      <option value="nopass">Aucun (reseau ouvert)</option>
                    </select>
                  </div>
                  {form.wifi_ssid && (
                    <div style={{marginTop: '8px', padding: '12px', background: '#16162a', borderRadius: '8px', fontSize: '13px', color: '#888'}}>
                      QR code genere pour : <strong style={{color: '#fff'}}>{form.wifi_ssid}</strong>
                      {form.wifi_password && <span> (protege par mot de passe)</span>}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* === STYLE === */}
            <div className="form-section">
              <h3>Style</h3>
              <div className="form-group">
                <label>Police</label>
                <select name="font_family" value={form.font_family} onChange={handleChange}>
                  <option value="Segoe UI">Segoe UI</option>
                  <option value="Inter">Inter</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Georgia">Georgia</option>
                </select>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : (page ? 'Mettre a jour' : 'Creer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DisplayPageModal;
